import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import {
  CHECK_ROTA_GENERATED_QUERY_KEY,
  INSTANCES_QUERY_KEY,
  REVISIONS_QUERY_KEY,
  ROTA_GENERATED_QUERY_KEY,
  ROTA_REMOVE_QUERY_KEY,
  ROTA_REMOVE_URL
} from './queryConstants'

type UseRotaRemoveReturn = {
  removeRota?: (values: RotaRemovePayload) => Promise<unknown>
  isLoadingRemoveRota: boolean
}

type RotaRemovePayload = {
  date: string
}

const useRotaRemove = (): UseRotaRemoveReturn => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const removeRotaFunction = async (
    values: RotaRemovePayload
  ): Promise<unknown> => {
    return postJSON({
      url: `${ROTA_REMOVE_URL}`,
      method: 'POST',
      json: values,
      isRota: true
    })
  }

  const { mutateAsync: removeRotaMutation, isLoading: isLoadingRemoveRota } =
    useMutation<unknown, unknown, RotaRemovePayload, unknown>(
      [ROTA_REMOVE_QUERY_KEY],
      removeRotaFunction,
      {
        onSuccess: () => {
          queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
          queryClient.invalidateQueries([ROTA_GENERATED_QUERY_KEY])
          queryClient.invalidateQueries([INSTANCES_QUERY_KEY])
          queryClient.invalidateQueries([CHECK_ROTA_GENERATED_QUERY_KEY])
          showToast({
            title: t('ROTA_REMOVED_SUCCESSFULLY'),
            type: 'success'
          })
        }
      }
    )

  const handleRemoveRota = async (values: RotaRemovePayload) => {
    try {
      const result = await removeRotaMutation(values)
      return result
    } catch (err: any) {
      showToast({
        title: t('ROTA_REMOVED_FAILED'),
        type: 'error'
      })
      return err
    }
  }

  return {
    removeRota: handleRemoveRota,
    isLoadingRemoveRota
  }
}

export default useRotaRemove
